/**
 * Shopper-flow decoration label helpers.
 *
 * P6 PR-A: re-keyed from legacy ``Decoration`` ids to
 * ``Placement`` ids. The ``decoration_records`` field name is
 * preserved on the wire + on the cart/order item shape (5 consumer
 * sites read it), but each entry's identity is now the placement's
 * id (sourced from ``CampaignProduct.placements`` via the
 * PR-3.5 SAFRS relationship). Consumers continue to read
 * ``decoration.id`` -- that value is now a placement_id, not a
 * legacy decoration_id.
 *
 * After PR-N1 the shopper flow calls
 * ``useResolveBatch().resolveBatch(pairs)`` to get a Map of
 * ``placement_id|color_sku -> { variant_id, kind, hint }`` and
 * passes it here to decide whether a decoration label appends.
 *
 * Sites that consume this module (kept in sync):
 *   - frontend/src/components/Order/Cart.vue
 *   - frontend/src/components/Order/OrderItemsTable.vue
 *   - frontend/src/components/Storefront/StorefrontViewShopper.vue
 *   - frontend/src/components/Storefront/StorefrontCart.vue
 *
 * Behavioral contract -- a decoration label appears IFF the
 * resolver returned ``hint=ok`` for the matching pair. The label
 * itself comes from ``decoration.description`` (or
 * ``decoration.name`` fallback) and is joined with the
 * zone slug (``decoration.placement_zone_slug``).
 * ``placement_not_found`` and ``no_variant`` hints suppress the
 * label silently -- the cart line still renders, just without the
 * decoration sub-line.
 *
 * Tests: ``frontend/src/utils/__tests__/decorationLabels.spec.js``.
 */

const pairKey = (placement_id, color_sku) =>
    `${placement_id}|${color_sku}`;

/**
 * Build the list of decoration label strings for a single cart line.
 *
 * @param {Object} campaignProduct -- the line's CampaignProduct row.
 *        Reads ``campaignProduct.decoration_records`` (each entry
 *        carries placement_id semantics under the ``id`` key per
 *        P6 PR-A).
 * @param {string} primaryAttributeValue -- the color identifier on
 *        the line (e.g. "Black", "WHITE"). Passed as ``color_sku``
 *        to the resolver.
 * @param {Map} resolverResults -- Map keyed by
 *        ``placement_id|color_sku`` from
 *        ``useResolveBatch().resolveBatch(...)``. May be empty if
 *        the resolver is mid-flight or failed.
 *
 * @returns {string[]} -- the decoration label strings to render
 *          beneath the cart line. Empty when no decoration routes
 *          for the line's color.
 */
export function buildCartDecorations(
    campaignProduct,
    primaryAttributeValue,
    resolverResults
) {
    const out = [];
    if (
        !campaignProduct ||
        !Array.isArray(campaignProduct.decoration_records) ||
        primaryAttributeValue == null ||
        primaryAttributeValue === ""
    ) {
        return out;
    }
    const results =
        resolverResults instanceof Map ? resolverResults : new Map();
    for (const decoration of campaignProduct.decoration_records) {
        if (!decoration || decoration.id == null) {
            continue;
        }
        const hit = results.get(
            pairKey(decoration.id, primaryAttributeValue)
        );
        if (!hit || hit.hint !== "ok") {
            continue;
        }
        const label = decoration.description || decoration.name || "";
        const zone = decoration.placement_zone_slug || "";
        out.push(zone ? `${label}: ${zone}` : label);
    }
    return out;
}

/**
 * Build the per-cell decoration detail string for the orders table.
 *
 * Mirrors the OrderItemsTable
 * ``getDecorationDetails(product, decoration)`` call shape.
 *
 * @param {Object} product -- the order_item row; reads
 *        ``product.campaign_product.decoration_records`` and
 *        ``product.primary_attribute_value``.
 * @param {Object} decoration -- the specific decoration row being
 *        rendered. Must carry ``id`` (placement_id per P6 PR-A).
 * @param {Map} resolverResults -- as in ``buildCartDecorations``.
 *
 * @returns {string} -- the label, or ``""`` to suppress.
 */
export function getDecorationDetails(
    product,
    decoration,
    resolverResults
) {
    if (
        !product ||
        !product.campaign_product ||
        !Array.isArray(product.campaign_product.decoration_records) ||
        product.campaign_product.decoration_records.length === 0 ||
        !decoration ||
        decoration.id == null
    ) {
        return "";
    }
    const results =
        resolverResults instanceof Map ? resolverResults : new Map();
    const hit = results.get(
        pairKey(decoration.id, product.primary_attribute_value)
    );
    if (!hit || hit.hint !== "ok") {
        return "";
    }
    const label = decoration.description || decoration.name || "";
    const zone = decoration.placement_zone_slug || "";
    return zone ? `${label}: ${zone}` : label;
}

/**
 * Walk a list of cart items / order items and produce the unique
 * (placement_id, color_sku) pairs the resolver needs.
 *
 * Each item is shaped ``{campaign_product OR campaignProduct,
 * primary_attribute_value OR primaryAttributeValue}`` so this helper
 * works against both the cart-row shape (Cart.vue / StorefrontCart)
 * and the order-item shape (OrderItemsTable).
 *
 * P6 PR-A: emits ``placement_id`` per pair (was ``decoration_id``).
 * Each ``decoration_records[*].id`` IS a placement_id under the new
 * SAFRS source (CampaignProduct.placements).
 *
 * @param {Array<Object>} items -- cart or order rows.
 * @returns {Array<{placement_id: number, color_sku: string}>}
 */
export function collectResolverPairs(items) {
    const seen = new Set();
    const out = [];
    if (!Array.isArray(items)) {
        return out;
    }
    for (const item of items) {
        if (!item) {
            continue;
        }
        const cp = item.campaign_product || item.campaignProduct;
        const color =
            item.primary_attribute_value ?? item.primaryAttributeValue;
        if (
            !cp ||
            !Array.isArray(cp.decoration_records) ||
            color == null ||
            color === ""
        ) {
            continue;
        }
        for (const decoration of cp.decoration_records) {
            if (!decoration || decoration.id == null) {
                continue;
            }
            const key = pairKey(decoration.id, color);
            if (seen.has(key)) {
                continue;
            }
            seen.add(key);
            out.push({
                placement_id: decoration.id,
                color_sku: color,
            });
        }
    }
    return out;
}

export { pairKey };
